<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { deleteServiceAction } from '@/api/functions/services/delete-service.action'
import { useMutationApi } from '@/composables/useApi'
import { useServiceCategories, useServices } from '@/composables/useServices'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const showDeleteModal = ref(false)
const totalItems = computed(() => pagination.value?.total || 0)
const { categoryOptions } = useServiceCategories()

const { services, pagination, currentPage, updateFilters, goToPage, refetchServices, isLoading } = useServices({
  page: 1,
})

const selectedService = ref<null | {
  id: string
  name: string
  slug: string
}>(null)

const statusOptions = [
  {
    label: 'فعال',
    value: 'ACTIVE',
  },
  {
    label: 'غیرفعال',
    value: 'INACTIVE',
  },
]

const filterFields = computed(() => [
  {
    type: 'input',
    key: 'name',
    label: 'نام سرویس را وارد کنید',
  },
  {
    type: 'input',
    key: 'slug',
    label: 'اسلاگ سرویس را وارد کنید',
  },
  {
    type: 'select',
    key: 'category_id',
    label: 'انتخاب دسته‌بندی',
    options: categoryOptions.value,
  },
  {
    type: 'select',
    key: 'status',
    label: 'انتخاب وضعیت',
    options: statusOptions,
  },
  {
    type: 'checkbox',
    key: 'is_featured',
    label: 'سرویس ویژه',
  },
])

const columns = [
  {
    key: 'index',
    label: '#',
    class: 'col-span-1 font-fa text-sm',
  },
  {
    key: 'code',
    label: 'شناسه',
    class: 'col-span-2 text-sm pe-6',
    truncate: true,
  },
  {
    key: 'name',
    label: 'نام سرویس',
    class: 'col-span-2 text-sm pe-4',
    truncate: true,
  },
  {
    key: 'slug',
    label: 'اسلاگ',
    class: 'col-span-2 text-sm pe-6',
    truncate: true,
  },
  {
    key: 'category',
    label: 'دسته‌بندی',
    class: 'col-span-2 text-sm',
  },
  {
    key: 'start_from',
    label: 'شروع قیمت',
    class: 'col-span-1 font-fa text-sm',
    type: 'number',
  },
  {
    key: 'statusLabel',
    label: 'وضعیت',
    class: 'col-span-1 text-sm',
  },
]

const itemsPerPage = computed(() => pagination.value?.perPage || 0)

const serviceRows = computed(() => {
  return services.value.map((service, index) => ({
    ...service,
    index: index + 1,
    category: service.categories?.map((category) => category.name).join('، ') || '-',
    provider: service.provider?.name || '-',
    serviceType: service.service_type?.name || '-',
    status: service.is_active ? 'ACTIVE' : 'INACTIVE',
    statusLabel: service.is_active ? 'فعال' : 'غیرفعال',
    start_from: service.start_from ?? '-',
  }))
})

// Handle Filters
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onApplyFilters = (values: Record<string, any>) => {
  const payload = { ...values }

  updateFilters(payload)

  const query = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  )

  router.push({
    path: route.path,
    query,
  })
}

const onResetFilters = () => {
  updateFilters({})
  goToPage(1)

  router.push({
    path: route.path,
    query: {},
  })
}

// Actions
const actions = [
  {
    label: 'مشاهده',
    handlerName: 'viewService',
  },
  {
    label: 'حذف',
    handlerName: 'deleteService',
  },
]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onViewService = (service: any) => {
  router.push(`/services/${service.slug}`)
}

// Delete
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onDeleteService = (service: any) => {
  selectedService.value = service
  showDeleteModal.value = true
}

const deleteMutation = useMutationApi({
  mutationFn: (slug: string) => deleteServiceAction(slug),

  onSuccess: (response) => {
    toast.success((response as any)?.meta?.message || 'سرویس با موفقیت حذف شد')

    showDeleteModal.value = false
    selectedService.value = null
    refetchServices()
  },

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleDeleteService = () => {
  if (!selectedService.value?.slug) return

  deleteMutation.mutate(selectedService.value.slug)
}

// // Mounted
// onMounted(() => {
//     if (Object.keys(route.query).length > 0) {
//         const { page, ...filters } = route.query

//         updateFilters(filters)

//         if (page) {
//             const pageNumber = Number(page)

//             if (!isNaN(pageNumber)) {
//                 currentPage.value = pageNumber
//             }
//         }
//     }
// })

// Watch Route Query
watch(
  () => route.query,
  (query) => {
    const { page, ...filters } = query

    updateFilters(filters)

    const pageNumber = Number(page)
    currentPage.value = page && !isNaN(pageNumber) ? pageNumber : 1
  },
  { immediate: true }
)
</script>

<template>
    <div class="w-full mb-12">

        <!-- Delete Modal -->
        <ConfirmModal v-model="showDeleteModal" title="حذف سرویس" message="آیا از حذف این سرویس اطمینان دارید؟"
            confirm-text="بله، حذف شود" cancel-text="بازگشت" @confirm="handleDeleteService"
            @cancel="showDeleteModal = false" />

        <div class="flex items-center justify-between">
            <h2 class="text-[22px] text-[#1b325a]">
                لیست خدمات
            </h2>

            <RouterLink to="/services/add">
                <Button :isAdmin="true" class="px-3! rounded-lg! h-8! py-0 text-[13px] font-medium">
                    افزودن سرویس جدید
                </Button>
            </RouterLink>
        </div>

        <!-- Filters -->

        <Filters :fields="filterFields" @apply="onApplyFilters" @reset="onResetFilters" class="mt-20 lg:mt-0" />

        <div class="flex flex-col gap-2 mt-3">

            <!-- Services -->

            <SkeletonTable v-if="isLoading" :rows="6" :cols="6" />

            <Table v-else :columns="columns" :rows="serviceRows" :actions="actions" @primaryAction="onViewService"
                @secondaryAction="onDeleteService" />

            <!-- Pagination -->

            <Pagination v-if="totalItems > 0" v-model:currentPage="currentPage" :itemsPerPage="itemsPerPage"
                :totalItems="totalItems" :isAdmin="true" @update:currentPage="goToPage" />

        </div>
    </div>
</template>
